import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageDropUploader from 'dashboard/components/common/ImageDropUploader';
import BlockStyleControls from './visual/BlockStyleControls';

/**
 * Everything about one block: what it says, what it is called, how it looks.
 *
 * `bare` drops the card chrome and the header row, for the block tree, which
 * draws its own row with the reorder and nesting controls on it. The list
 * view still wants the chrome, so both exist rather than one of them being
 * duplicated somewhere else.
 */
export default function ComponentEditor({
  component, catalog, brandId, onPatch, onDelete, bare = false,
}) {
  const versionsForType = useMemo(
    () => (catalog?.components || []).find((c) => c.type === component.type)?.versions || ['v1'],
    [catalog, component.type],
  );

  const patchContent = (key, value) => {
    onPatch({ content: { ...(component.content || {}), [key]: value } });
  };
  const patchStyles = (key, value) => {
    onPatch({ styles: { ...(component.styles || {}), [key]: value } });
  };

  const isText = ['heading', 'text', 'button'].includes(component.type);

  const renderTypeEditor = () => {
    const c = component.content || {};
    switch (component.type) {
      case 'heading':
        return (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px'>
            <TextField
              size='small'
              label='Text'
              value={c.text || ''}
              onChange={(e) => patchContent('text', e.target.value)}
              sx={{ flex: 1 }}
            />
            <FormControl size='small' sx={{ maxWidth: 120, minWidth: 100 }}>
              <InputLabel>Level</InputLabel>
              <Select
                label='Level'
                value={c.level || 'h2'}
                onChange={(e) => patchContent('level', e.target.value)}
              >
                <MenuItem value='h1'>h1</MenuItem>
                <MenuItem value='h2'>h2</MenuItem>
                <MenuItem value='h3'>h3</MenuItem>
                <MenuItem value='h4'>h4</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );
      case 'text':
        return (
          <TextField
            size='small'
            label='Text'
            multiline
            minRows={3}
            value={c.text || ''}
            onChange={(e) => patchContent('text', e.target.value)}
            fullWidth
          />
        );
      case 'image':
        return (
          <Stack spacing='8px'>
            <ImageDropUploader
              brandId={brandId}
              currentSrc={c.src}
              onUploaded={(url) => patchContent('src', url)}
            />
            {/* The URL field is gone: the uploader above is how an image
                gets here, and a second way to set the same value invited
                pasting a web address that only works while someone else's
                site keeps hosting it. Alt text stays — it is the one thing
                about an image that has to be typed. */}
            <TextField
              size='small'
              label='Alt text'
              value={c.alt || ''}
              onChange={(e) => patchContent('alt', e.target.value)}
              helperText='Describes the image for screen readers and when it fails to load.'
              fullWidth
            />
            <TextField
              size='small'
              label='Caption'
              value={c.caption || ''}
              onChange={(e) => patchContent('caption', e.target.value)}
              fullWidth
            />
            <Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                Widest it gets — {component.styles?.maxWidth ? `${component.styles.maxWidth}px` : 'full width'}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={1200}
                step={20}
                value={Number(component.styles?.maxWidth) || 0}
                onChange={(_e, v) => patchStyles('maxWidth', v === 0 ? '' : v)}
                valueLabelDisplay='auto'
              />
            </Box>
          </Stack>
        );
      case 'button':
        return (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px'>
            <TextField
              size='small'
              label='Label'
              value={c.text || ''}
              onChange={(e) => patchContent('text', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size='small'
              label='Goes to'
              value={c.url || ''}
              onChange={(e) => patchContent('url', e.target.value)}
              placeholder='/contact'
              sx={{ flex: 1 }}
            />
            <FormControl size='small' sx={{ maxWidth: 130, minWidth: 110 }}>
              <InputLabel>Style</InputLabel>
              <Select
                label='Style'
                value={c.variant || 'primary'}
                onChange={(e) => patchContent('variant', e.target.value)}
              >
                <MenuItem value='primary'>Solid</MenuItem>
                <MenuItem value='secondary'>Dark</MenuItem>
                <MenuItem value='outline'>Outlined</MenuItem>
                <MenuItem value='ghost'>Plain</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );
      case 'video':
        return (
          <TextField
            size='small'
            label='Video URL'
            value={c.src || ''}
            onChange={(e) => patchContent('src', e.target.value)}
            placeholder='https://youtu.be/…'
            fullWidth
          />
        );
      case 'grid':
        return (
          <Stack spacing='10px'>
            <Typography variant='caption' color='text.secondary'>
              Put cards, pictures or text inside this grid and they sit side by
              side. On a phone they stack, whatever you choose here.
            </Typography>
            <FormControl size='small' fullWidth>
              <InputLabel>Across</InputLabel>
              <Select
                label='Across'
                value={Number(c.columns) || 3}
                onChange={(e) => patchContent('columns', e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <MenuItem key={n} value={n}>{n} {n === 1 ? 'column' : 'columns'}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                Space between — {c.gap ?? 24}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={64}
                step={4}
                value={Number(c.gap ?? 24)}
                onChange={(_e, v) => patchContent('gap', v)}
                valueLabelDisplay='auto'
              />
            </Box>
            <FormControl size='small' fullWidth>
              <InputLabel>Line things up</InputLabel>
              <Select
                label='Line things up'
                value={c.align || 'stretch'}
                onChange={(e) => patchContent('align', e.target.value)}
              >
                <MenuItem value='stretch'>Same height</MenuItem>
                <MenuItem value='start'>From the top</MenuItem>
                <MenuItem value='center'>Middle</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );
      case 'card':
        return (
          <Stack spacing='10px'>
            <Typography variant='caption' color='text.secondary'>
              A box to group things — a heading, some text, a price, a button.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={!c.plain}
                  onChange={(e) => patchContent('plain', !e.target.checked)}
                />
              }
              label={<Typography variant='body2'>Show the box outline</Typography>}
            />
          </Stack>
        );
      case 'divider':
      case 'spacer':
        return <Typography variant='caption' color='text.secondary'>Nothing to fill in.</Typography>;
      default:
        return (
          <Typography variant='caption' color='text.secondary'>
            No editor for “{component.type}” yet.
          </Typography>
        );
    }
  };

  const body = (
    <Stack spacing='12px'>
      {renderTypeEditor()}

      {/* Naming a block is how a tree of twelve of them stays readable. The
          name is never shown to visitors — it is a label for whoever is
          building the page, which is why it sits with the settings rather
          than with the words. */}
      <TextField
        size='small'
        label='Call this block'
        value={component.name || ''}
        onChange={(e) => onPatch({ name: e.target.value })}
        placeholder={component.type}
        helperText='Only you see this — it labels the block in the list.'
        fullWidth
      />

      <Box>
        <Typography
          variant='caption'
          fontWeight='700'
          color='text.secondary'
          textTransform='uppercase'
          display='block'
          mb='8px'
        >
          Appearance
        </Typography>
        <BlockStyleControls
          styles={component.styles || {}}
          onChange={(styles) => onPatch({ styles })}
          showText={isText}
        />
      </Box>
    </Stack>
  );

  if (bare) return body;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        p: '10px',
        mb: '8px',
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='8px' gap='8px' flexWrap='wrap'>
        <Stack direction='row' spacing='6px'>
          <Chip size='small' color='primary' label={component.type} />
          <Chip size='small' variant='outlined' label={component.version || 'v1'} />
        </Stack>
        <Stack direction='row' spacing='4px' alignItems='center'>
          {versionsForType.length > 1 && (
            <Select
              size='small'
              value={component.version || 'v1'}
              onChange={(e) => onPatch({ version: e.target.value })}
              sx={{ width: 90 }}
            >
              {versionsForType.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          )}
          <IconButton size='small' color='error' onClick={onDelete}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>
      {body}
    </Box>
  );
}
